import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorFallback } from "./ErrorFallback";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSafeErrorMessage, getLogErrorMessage } from "@/lib/errorUtils";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * ErrorBoundary component - catches React rendering errors
 * 
 * Features:
 * - Production-safe error messages (no stack traces exposed)
 * - Development-friendly detailed errors
 * - Uses centralized error utilities
 * - Provides recovery options
 * - Logs errors properly for monitoring
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourApp />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Use centralized error logging
    const logMessage = getLogErrorMessage(error);
    console.error("ErrorBoundary caught:", logMessage);
    console.error("Component stack:", errorInfo.componentStack);
    
    // In production, send to error tracking service
    // TODO: Integrate with Sentry, LogRocket, or similar
    if (import.meta.env.PROD) {
      // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
      // For now, just log to console
      console.error('Production error:', {
        message: error.message,
        name: error.name,
        componentStack: errorInfo.componentStack,
      });
    }
    
    this.setState({ errorInfo });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Get safe error message using centralized utility
      const safeError = getSafeErrorMessage(this.state.error);

      // In production, show clean error UI with safe messages
      if (import.meta.env.PROD) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="max-w-md w-full space-y-6 text-center">
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold">Something went wrong</h1>
                <p className="text-muted-foreground">
                  {safeError.message}
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button onClick={this.resetError} variant="default">
                  Try Again
                </Button>
                <Button onClick={() => window.location.href = "/"} variant="outline">
                  Go Home
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                If this problem persists, please contact support.
              </p>
            </div>
          </div>
        );
      }

      // In development, show detailed error info for debugging
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full space-y-6">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
              <h1 className="text-2xl font-bold text-destructive">Development Error</h1>
              <p className="text-muted-foreground">
                {safeError.message}
              </p>
            </div>
            
            {/* Error details - only in development */}
            <div className="bg-muted p-4 rounded-lg text-left overflow-auto max-h-96 space-y-4">
              <div>
                <p className="text-sm font-semibold mb-2">Error:</p>
                <p className="text-sm font-mono text-destructive">
                  {this.state.error.toString()}
                </p>
              </div>
              
              {safeError.code && (
                <div>
                  <p className="text-sm font-semibold mb-2">Code:</p>
                  <p className="text-sm font-mono">{safeError.code}</p>
                </div>
              )}
              
              {this.state.errorInfo && (
                <div>
                  <p className="text-sm font-semibold mb-2">Component Stack:</p>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
              
              {this.state.error.stack && (
                <div>
                  <p className="text-sm font-semibold mb-2">Stack Trace:</p>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button onClick={this.resetError} variant="default">
                Try Again
              </Button>
              <Button onClick={() => window.location.href = "/"} variant="outline">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
